import React, { useState, useMemo, useEffect } from 'react';
import { View, ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import SheetModal from '@/components/General/SheetModal';
import Input from '@/components/General/inputs/Input';
import SelectInput from '@/components/General/inputs/SelectInput';
import SolidButton from '@/components/General/SolidButton';
import { Tooltip } from '@/components/General/Tooltip';
import Unicon from '@/components/General/Unicon';
import { Query } from '@/utils/react-query/Queries';
import { getPeople } from '@/data/people';
import { DateTime } from 'luxon';
import Toast from 'react-native-toast-message';
import { generatePDFForCheckIn } from '@/utils/generatePDF';
import SheetSelector from '../General/inputs/SheetSelector';
import useCheckInPrintStore from '@/data/store/useCheckInPrintStore';

const PrintableCheckInModal = ({ isOpen, onClose, eventInstance }) => {
    const [title, setTitle] = useState(eventInstance?.Event?.name ? `${eventInstance.Event.name} Check-In Sheet` : 'Check-In Sheet');
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [grouping, setGrouping] = useState('None');
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [layout, setLayout] = useState('Compact');
    const [minAge, setMinAge] = useState('');
    const [maxAge, setMaxAge] = useState('');
    const [selectedQuickFilter, setSelectedQuickFilter] = useState(
        eventInstance?.Event?.CheckInFilter ? { id: eventInstance.Event.CheckInFilter.id, name: eventInstance.Event.CheckInFilter.name } : null
    );
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [isGenerating, setIsGenerating] = useState(false);

    useEffect(() => {
        if (!isOpen || !eventInstance?.id) return;

        const loadSaved = async () => {
            const store = useCheckInPrintStore.getState();
            if (!store._hasHydrated) {
                await store.hydrate();
            }
            const saved = store.getSelections(eventInstance.id);
            if (!saved) return;

            setTitle(saved.title || (eventInstance?.Event?.name ? `${eventInstance.Event.name} Check-In Sheet` : 'Check-In Sheet'));
            setSelectedLocation(saved.selectedLocation ?? null);
            setGrouping(saved.grouping ?? 'None');
            setSelectedRoles(saved.selectedRoles ?? []);
            setLayout(saved.layout ?? 'Compact');
            setMinAge(saved.minAge ?? '');
            setMaxAge(saved.maxAge ?? '');
            setSelectedQuickFilter(saved.selectedQuickFilter ?? null);
            setSelectedColumns(saved.selectedColumns ?? []);
        };

        loadSaved();
    }, [isOpen, eventInstance?.id]);

    const { data: quickFilters = [] } = Query.quickFilters.all({ options: { enabled: isOpen } });
    const { data: personCustomFields = [] } = Query.personCustomFields.all({ options: { enabled: isOpen } });
    const { data: organization } = Query.organization.byId();

    const locations = eventInstance?.Locations || [];
    const hasLocations = locations.length > 0;

    const locationOptions = useMemo(() => {
        return locations.map(loc => ({ label: loc.name, value: loc.id }));
    }, [locations]);

    const groupingOptions = [
        { label: 'None', value: 'None' },
        { label: 'Age', value: 'Age' },
        { label: 'Role', value: 'Role' }
    ];

    const layoutOptions = [
        { label: 'Compact', value: 'Compact' },
        { label: 'Full Page', value: 'Full Page' }
    ];

    const columnOptions = useMemo(() => {
        const baseFields = [
            { label: 'Phone', value: 'phone', type: 'base' },
            { label: 'Email', value: 'email', type: 'base' },
            { label: 'Age', value: 'age', type: 'base' },
            { label: 'Role', value: 'role', type: 'base' },
            { label: 'Gender', value: 'gender', type: 'base' },
        ];
        const customFieldOptions = personCustomFields.map(cf => ({
            label: cf.name,
            value: `cf_${cf.id}`,
            type: 'custom',
            customFieldId: cf.id
        }));
        return [...baseFields, ...customFieldOptions];
    }, [personCustomFields]);

    const quickFilterOptions = useMemo(() => {
        const options = quickFilters.map(qf => ({ label: qf.name, value: qf.id }));
        if (eventInstance?.Event?.CheckInFilter) {
            const exists = options.find(o => o.value === eventInstance.Event.CheckInFilter.id);
            if (!exists) {
                options.unshift({ label: eventInstance.Event.CheckInFilter.name + ' (Event Default)', value: eventInstance.Event.CheckInFilter.id });
            }
        }
        return options;
    }, [quickFilters, eventInstance]);

    const getQuickFilterLabel = (filter) => {
        if (!filter) return '';
        if (filter.name) return filter.name;
        const found = quickFilters.find(qf => qf.id === filter.id || qf.id === filter.value || qf.id === filter);
        if (found) return found.name;
        if (eventInstance?.Event?.CheckInFilter?.id === (filter.id || filter.value || filter)) {
            return eventInstance.Event.CheckInFilter.name;
        }
        return '';
    };

    const getFilterForQuery = () => {
        if (selectedQuickFilter) {
            const filter = quickFilters.find(qf => qf.id === selectedQuickFilter?.id);
            if (filter) {
                return { filters: filter.filterJSON, logic: filter.logic };
            }
            if (eventInstance?.Event?.CheckInFilter && (selectedQuickFilter.value === eventInstance.Event.CheckInFilter.id || selectedQuickFilter?.id === eventInstance.Event.CheckInFilter.id)) {
                return { filters: eventInstance.Event.CheckInFilter.filterJSON, logic: eventInstance.Event.CheckInFilter.logic };
            }
        }
        return { filters: {}, logic: 'AND' };
    };

    const calculateAge = (dateOfBirth) => {
        if (!dateOfBirth) return null;
        const dob = DateTime.fromISO(dateOfBirth);
        if (!dob.isValid) return null;
        const now = DateTime.now();
        const age = Math.floor(now.diff(dob, 'years').years);
        return age >= 0 ? age : null;
    };

    const groupPeopleByAge = (people, minAgeVal, maxAgeVal) => {
        const min = parseInt(minAgeVal, 10);
        const max = parseInt(maxAgeVal, 10);
        
        if (isNaN(min) || isNaN(max) || min > max) {
            return [['All Attendees', people]];
        }

        const groups = {};
        for (let age = min; age <= max; age++) {
            groups[`${age} Year Olds`] = [];
        }

        people.forEach(person => {
            const age = calculateAge(person.dateOfBirth);
            if (age !== null && age >= min && age <= max) {
                groups[`${age} Year Olds`].push({ ...person, calculatedAge: age });
            }
        });

        return Object.entries(groups).filter(([, members]) => members.length > 0);
    };

    const groupPeopleByRole = (people, roles) => {
        const groups = {};
        
        roles.forEach(role => {
            groups[role.name] = [];
        });

        people.forEach(person => {
            const personRoles = person.Roles?.map(r => r.name) || [];
            if (personRoles.length === 0) return;

            for (const role of roles) {
                if (personRoles.includes(role.name)) {
                    groups[role.name].push(person);
                    break;
                }
            }
        });

        return Object.entries(groups).filter(([, members]) => members.length > 0);
    };

    const generatePDF = async (people) => {
        setIsGenerating(true);

        try {
            let groupedData;
            
            if (grouping === 'Age') {
                groupedData = groupPeopleByAge(people, minAge, maxAge);
            } else if (grouping === 'Role') {
                groupedData = groupPeopleByRole(people, selectedRoles);
            } else {
                groupedData = [['All Attendees', people]];
            }

            const locationName = selectedLocation 
                ? selectedLocation?.label 
                : null;

            await generatePDFForCheckIn({
                groupedData,
                locationName,
                title,
                eventInstance,
                organization,
                layout,
                selectedColumns,
                personCustomFields
            });

            Toast.show({ type: 'success', text2: 'Check-in sheet generated successfully!' });
            onClose();
        } catch (error) {
            console.error('PDF Generation Error:', error);
            Toast.show({ type: 'error', text2: 'Failed to generate PDF. Please try again.' });
        } finally {
            setIsGenerating(false);
        }
    };

    const handleGenerate = async () => {
        if (!selectedQuickFilter) {
            Toast.show({ type: 'error', text2: 'Please select a Quick Filter' });
            return;
        }

        if (grouping === 'Role' && selectedRoles.length === 0) {
            Toast.show({ type: 'error', text2: 'Please select at least one role for grouping' });
            return;
        }

        if (grouping === 'Age') {
            const min = parseInt(minAge, 10);
            const max = parseInt(maxAge, 10);
            if (isNaN(min) || isNaN(max)) {
                Toast.show({ type: 'error', text2: 'Please enter both min and max age values' });
                return;
            }
            if (min > max) {
                Toast.show({ type: 'error', text2: 'Min age cannot be greater than max age' });
                return;
            }
            if (max - min > 20) {
                Toast.show({ type: 'error', text2: 'Age range cannot exceed 20 years' });
                return;
            }
        }

        if (eventInstance?.id) {
            await useCheckInPrintStore.getState().saveSelections(eventInstance.id, {
                title,
                selectedLocation,
                grouping,
                selectedRoles,
                layout,
                minAge,
                maxAge,
                selectedQuickFilter,
                selectedColumns,
            });
        }

        setIsGenerating(true);

        try {
            const { filters, logic } = getFilterForQuery();

            let allPeople = [];
            let page = 0;
            let hasMore = true;

            while (hasMore) {
                const result = await getPeople({ pageParam: page, filters, filterLogic: logic });
                const pageData = result?.data || [];
                allPeople = [...allPeople, ...pageData];
                
                hasMore = pageData.length === 50;
                page++;
                
                if (page > 100) break;
            }

            const people = allPeople;

            if (!people || people.length === 0) {
                Toast.show({ type: 'info', text2: 'No people found matching the selected filter' });
                setIsGenerating(false);
                return;
            }

            await generatePDF(people);
        } catch (error) {
            console.error('Error fetching people:', error);
            Toast.show({ type: 'error', text2: 'Failed to fetch people. Please try again.' });
            setIsGenerating(false);
        }
    };

    return (
        <SheetModal
            isOpen={isOpen}
            onClose={onClose}
            title="Generate Check-In Sheet"
            cancelText="Cancel"
        >
            <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled"
>
                <Input
                    title="Title"
                    value={title}
                    onUpdate={setTitle}
                    placeholder="Enter sheet title"
                />

                <SelectInput
                    title="Location"
                    value={selectedLocation}
                    onUpdate={(val) => {
                        const loc = locations.find(l => l.id === val);
                        setSelectedLocation({label: loc?.name, id: loc?.id})
                    }}
                    options={locationOptions ?? []}
                    placeholder={hasLocations ? "Select a location" : "No locations available"}
                    disabled={!hasLocations}
                />

                <SelectInput
                    title="Grouping"
                    value={grouping}
                    onUpdate={setGrouping}
                    options={groupingOptions ?? []}
                    hideClearButton
                />

                {grouping === 'Role' && (
                    <SheetSelector
                        helpText="People without selected roles will appear in 'Others' section"
                        multiple 
                        type="roles" 
                        title="Select Roles to Group By" 
                        value={selectedRoles || []} 
                        onChange={(selected_items) => {
                            setSelectedRoles(selected_items)
                        }} 
                    />
                )}

                {grouping === 'Age' && (
                    <View style={styles.ageRangeRow}>
                        <View style={styles.ageInputWrapper}>
                            <Input
                                title="Min Age"
                                value={minAge}
                                onUpdate={setMinAge}
                                placeholder="e.g. 3"
                                keyboardType="numeric"
                            />
                        </View>
                        <View style={styles.ageInputWrapper}>
                            <Input
                                title="Max Age"
                                value={maxAge}
                                onUpdate={setMaxAge}
                                placeholder="e.g. 12"
                                keyboardType="numeric"
                            />
                        </View>
                    </View>
                )}

                <View style={styles.layoutRow}>
                    <View style={styles.layoutSelectWrapper}>
                        <SelectInput
                            title="Layout"
                            value={layout}
                            onUpdate={setLayout}
                            options={layoutOptions}
                            hideClearButton
                        />
                    </View>
                    <View style={styles.tooltipWrapper}>
                        <Tooltip 
                            appearOnTouch
                            title="Compact: Groups share pages when space allows. Full Page: Each group starts on a new page."
                        >
                            <Unicon name="help-circle-outline" size={20} colorVariant="textSecondary" />
                        </Tooltip>
                    </View>
                </View>

                <SelectInput
                    title="Quick Filter"
                    value={selectedQuickFilter ? { label: getQuickFilterLabel(selectedQuickFilter), value: selectedQuickFilter.id || selectedQuickFilter.value || selectedQuickFilter } : null}
                    onUpdate={(val) => {
                        if (!val) {
                            setSelectedQuickFilter(null);
                            return;
                        }
                        const filter = quickFilters.find(qf => qf.id === val) || 
                            (eventInstance?.Event?.CheckInFilter?.id === val ? eventInstance.Event.CheckInFilter : null);
                        setSelectedQuickFilter(filter ? { id: filter.id, name: filter.name } : { id: val, name: val });
                    }}
                    options={quickFilterOptions ?? []}
                    placeholder="Select a filter..."
                    helpText="Determines which people appear on the check-in sheet"
                />

                <SelectInput
                    title="Additional Columns"
                    value={selectedColumns}
                    onUpdate={(val) => {
                        if (Array.isArray(val) && val.length > 3) {
                            return;
                        }
                        setSelectedColumns(val || []);
                    }}
                    options={columnOptions}
                    multiple
                    placeholder="Select up to 3 fields..."
                    helpText="Choose up to 3 additional fields to display in the roster"
                />

                <View style={styles.buttonContainer}>
                    <SolidButton
                        text="Generate PDF"
                        iconName="document-text"
                        type="primary"
                        onPress={handleGenerate}
                        processing={isGenerating}
                        disabled={isGenerating || !selectedQuickFilter}
                        width="100%"
                    />
                </View>
            </ScrollView>
        </SheetModal>
    );
};

const styles = StyleSheet.create(() => ({
    scrollContent: {
        paddingBottom: 40
    },
    layoutRow: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 8
    },
    layoutSelectWrapper: {
        flex: 1
    },
    tooltipWrapper: {
        paddingBottom: 18
    },
    buttonContainer: {
        marginTop: 20
    },
    ageRangeRow: {
        flexDirection: 'row',
        gap: 12
    },
    ageInputWrapper: {
        flex: 1
    }
}));

export default PrintableCheckInModal;
